import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../api';
import { PageTitle, LoadingText } from '../components/Style';
import { Card } from '../components/FormStyle';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import { BADGE_CONFIG } from '../components/UserBadge';

var Container = styled.div`
  box-sizing: border-box;
  max-width: 410px;
  margin: 0 auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

var NotificationCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  background: #fff;
  border: 1px solid #ddd;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
`;

var UnreadDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: black;
  flex-shrink: 0;
  margin-top: 0.35em;
`;

var ReadDot = styled.div`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
`;

var NotificationText = styled.div`
  font-size: 0.85rem;
  line-height: 1.4;
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
`;

var ActorName = styled(Link)`
  font-weight: 900;
  text-decoration: none;
  color: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

var MediaTitle = styled(Link)`
  font-weight: 700;
  text-decoration: none;
  color: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

var TimeText = styled.div`
  font-size: 0.7rem;
  color: #999;
  margin-top: 0.15em;
`;

function relativeTime(dateString) {
  var now = new Date();
  var date = new Date(dateString);
  var seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'just now';
  var minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + 'm ago';
  var hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + 'h ago';
  var days = Math.floor(hours / 24);
  if (days < 7) return days + 'd ago';
  var weeks = Math.floor(days / 7);
  return weeks + 'w ago';
}

function Notifications() {
  useEffect(function () {
    document.title = 'Notifications | HeavyRotation';
  }, []);

  var navigate = useNavigate();
  var readMapState = useState({});
  var readMap = readMapState[0];
  var setReadMap = readMapState[1];

  // Mark notifications as viewed on mount — clears the nav badge
  // without affecting individual read/unread dots
  useEffect(function () {
    // Clear badge immediately so user sees instant feedback
    window.dispatchEvent(new Event('notifications-read'));
    api.post('/api/notifications/viewed').catch(function () {});
  }, []);

  var fetchPage = useCallback(function (cursor) {
    var url = '/api/notifications?limit=20';
    if (cursor) url += '&before=' + encodeURIComponent(cursor);
    return api.get(url).then(function (result) {
      return {
        posts: result.data.notifications || [],
        hasMore: result.data.hasMore,
        nextCursor: result.data.nextCursor,
      };
    });
  }, []);

  var scroll = useInfiniteScroll(fetchPage, []);
  var notifications = scroll.posts;
  var isLoading = scroll.loading;
  var loadingMore = scroll.loadingMore;
  var sentinelRef = scroll.sentinelRef;

  var hasUnread = notifications.some(function (n) {
    if (readMap[n.id] !== undefined) return !readMap[n.id];
    return !n.isRead;
  });

  function handleMarkAllRead() {
    var map = {};
    for (var i = 0; i < notifications.length; i++) {
      map[notifications[i].id] = true;
    }
    setReadMap(map);
    api.post('/api/notifications/read').catch(function () {});
    window.dispatchEvent(new Event('notifications-read'));
  }

  function handleNotificationClick(id) {
    setReadMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) next[key] = prev[key];
      }
      next[id] = true;
      return next;
    });
    api.post('/api/notifications/' + id + '/read').catch(function () {});
    window.dispatchEvent(new Event('notifications-read'));
  }

  if (isLoading) {
    return (
      <Container>
        <PageTitle style={{ marginTop: '2rem' }}>Notifications</PageTitle>
        <LoadingText>Loading...</LoadingText>
      </Container>
    );
  }

  if (notifications.length === 0) {
    return (
      <Container>
        <PageTitle style={{ marginTop: '2rem' }}>Notifications</PageTitle>
        <p style={{ color: '#999', fontSize: '0.85rem' }}>No notifications yet.</p>
      </Container>
    );
  }

  return (
    <Container>
      <PageTitle style={{ marginTop: '2rem' }}>Notifications</PageTitle>

      {hasUnread && (
        <div style={{ width: '100%', textAlign: 'right', marginBottom: '0.5rem' }}>
          <button
            onClick={handleMarkAllRead}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              fontSize: '0.8rem',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
              fontFamily: 'inherit',
            }}
          >
            Mark all as read
          </button>
        </div>
      )}

      {notifications.map(function (n) {
        if (!n.actor) return null;
        var message;
        var postLink = '/post/' + n.postId + (n.commentId ? '#comment-' + n.commentId : '');
        var profileLink = '/profile/' + n.actor.username;
        var cardLink;
        // A reaction or comment on a list item names its list after the
        // song: "reacted to Illmatic in your list Desert Island Albums".
        var isListItem = !!(n.listTitle && n.mediaTitle);
        var listSuffix = isListItem ? ' in your list ' + n.listTitle : null;

        if (n.type === 'badge_awarded') {
          message = null;
          cardLink = profileLink;
        } else if (n.type === 'new_follower') {
          message = ' started following you';
          cardLink = profileLink;
        } else if (n.type === 'repost_on_post') {
          message = ' reposted your post ';
          cardLink = postLink;
        } else if (n.type === 'reaction_on_post') {
          message = isListItem ? ' reacted to ' : ' reacted to your post ';
          cardLink = postLink;
        } else if (n.type === 'like_on_comment') {
          message = ' liked your comment on ';
          cardLink = postLink;
        } else if (n.type === 'reply_to_comment') {
          message = ' replied to your comment on ';
          cardLink = postLink;
        } else if (n.type === 'mention') {
          message = ' mentioned you in ';
          cardLink = postLink;
        } else {
          message = isListItem ? ' commented on ' : ' commented on your post ';
          cardLink = postLink;
        }
        var showListSuffix =
          isListItem && (n.type === 'reaction_on_post' || n.type === 'comment_on_post');

        var isRead = readMap[n.id] !== undefined ? readMap[n.id] : n.isRead;

        return (
          <NotificationCard
            key={n.id}
            onClick={function () {
              handleNotificationClick(n.id);
              navigate(cardLink);
            }}
          >
            {!isRead ? <UnreadDot /> : <ReadDot />}
            <NotificationText>
              {n.type === 'badge_awarded' ? (
                <>
                  You earned the{' '}
                  <span style={{ fontWeight: 900 }}>
                    {(BADGE_CONFIG[n.metadata] || {}).label || 'new'}
                  </span>{' '}
                  badge!
                </>
              ) : (
                <>
                  <ActorName
                    to={profileLink}
                    onClick={function (e) {
                      e.stopPropagation();
                      handleNotificationClick(n.id);
                    }}
                  >
                    {n.actor.username}
                  </ActorName>
                  {message}
                  {n.mediaTitle && (
                    <MediaTitle
                      to={postLink}
                      onClick={function (e) {
                        e.stopPropagation();
                        handleNotificationClick(n.id);
                      }}
                    >
                      {n.mediaTitle}
                    </MediaTitle>
                  )}
                  {showListSuffix && listSuffix}
                </>
              )}
              <TimeText>{relativeTime(n.createdAt)}</TimeText>
            </NotificationText>
          </NotificationCard>
        );
      })}
      <div ref={sentinelRef} />
      {loadingMore && <LoadingText>Loading more...</LoadingText>}
    </Container>
  );
}

export default Notifications;
